/*
 * ॐ नमः शिवाय
 * ಓಂ ನಮಃ ಶಿವಾಯ
 * ఓం నమః శివాయ
 * ஓம் நம சிவாய
 *
 * Copyright (c) 2025 Rudra Coresoft Technologies Private Limited
 *
 * This software is licensed under the VIJAYA 1.0 License.
 *
 * You are free to use, modify, and distribute this software under the terms 
 * of the VIJAYA 1.0 License.
 *
 * MANDATORY ATTRIBUTION:
 * 1. The attribution to Rudra Coresoft Technologies Private Limited in this 
 *    copyright notice must not be removed even after modifications.
 * 2. For Free License usage, the footer phrases "Powered by arcee.io" and 
 *    "Developed by Rudra Coresoft" (plus logos) must remain visible.
 *
 * See the LICENSE file for the full text.
 */

/**
 * routeDwell
 * ----------
 * Decides which feed cards someone has actually stopped on: a card counts once
 * its map has stayed in view (at least VISIBLE_FRACTION of it on screen) for
 * ROUTE_DWELL_MS while the list is at rest. Any scroll movement cancels every
 * timer, so a fast scroll never qualifies a card, whatever the delay.
 *
 * Pure bookkeeping with the time passed in — the caller owns the clock and the
 * timer. This is the web port of the Android `RouteDwellTracker`
 * (upvibe: presentation/home/RouteDwellTracker.java), kept deliberately
 * one-to-one with it so both platforms behave the same and can be reasoned
 * about together.
 */

/** How long a card must stay in view, with the list at rest, before its route is requested. */
export const ROUTE_DWELL_MS = 1000;

/** Share of a card's map area that must be on screen for the card to count as in view. */
export const VISIBLE_FRACTION = 0.5;

/** Route downloads at once. The server timed out when five ran in parallel. */
export const MAX_PARALLEL_ROUTES = 1;

/** How long the list must be quiet after the last scroll event to count as at rest. */
export const SCROLL_IDLE_MS = 140;

export function createRouteDwellTracker(dwellMs = ROUTE_DWELL_MS) {
  /** Cards in view, top to bottom, as last reported. */
  let inView = [];
  /** When each card in view started its current, uninterrupted stay. */
  const inViewSince = new Map();

  /** Milliseconds left before `id` reaches the delay (0 = dwelled). */
  function remaining(id, nowMs) {
    if (!inViewSince.has(id)) return dwellMs;
    // Clamped so a wall clock set backwards can't postpone a card past one full delay.
    return Math.max(0, Math.min(dwellMs, dwellMs - (nowMs - inViewSince.get(id))));
  }

  return {
    /** The list is dragging or flinging (or nobody is looking): no card is being looked at. */
    onListMoving() {
      inView = [];
      inViewSince.clear();
    },

    /**
     * The list is at rest with these cards in view, top to bottom. Cards already
     * in view keep their start time; cards no longer listed lose theirs.
     */
    onInView(ids, nowMs) {
      inView = Array.from(ids);
      for (const id of Array.from(inViewSince.keys())) {
        if (!inView.includes(id)) inViewSince.delete(id);
      }
      for (const id of inView) {
        if (!inViewSince.has(id)) inViewSince.set(id, nowMs);
      }
    },

    /** Cards that have stayed in view for the full delay, top to bottom. */
    dwelled(nowMs) {
      return inView.filter((id) => remaining(id, nowMs) === 0);
    },

    /** Milliseconds until the next card in view reaches the delay, or -1 when none is waiting. */
    millisUntilNext(nowMs) {
      let next = -1;
      for (const id of inView) {
        const left = remaining(id, nowMs);
        if (left > 0 && (next < 0 || left < next)) next = left;
      }
      return next;
    },
  };
}
